<template>
  <div>
    <div class="mt-2 mb-6 pb-6 border-b-2 border-grey-light flex flex-wrap items-center">
      <div>
        <h1 class="text-grey-darker text-xl font-semibold leading-normal">
          Associate Scheme - Students
        </h1>
      </div>
    </div>

    <loader v-if="!results" />

    <panel v-else-if="results.data.length === 0 && !filtered" class="pt-6">
      <h2 class="font-semibold mb-4">No registered students found</h2>
      <p class="my-4">Staff registrations and progress will appear here.</p>
    </panel>

    <panel v-else class="pt-6">
      <h2 class="font-semibold mb-4">Your Staff Progress</h2>
      <hr class="w-full border-b border-grey-light mb-6" />
      <div class="flex flex-wrap text-xs uppercase mb-8">
        <div class="w-full lg:w-1/4 pr-6">
          <label class="block mb-2">Search</label>
          <input type="text" v-model="searchParams.q" placeholder="Search" class="w-full border hover:border-grey-darker p-3" />
        </div>
        <div class="w-full lg:w-1/4 pr-6">
          <label class="block mb-2">Qualification</label>
          <select v-model="searchParams.course_id" class="w-full border hover:border-grey-darker p-3">
            <option :value="null">All</option>
            <option v-for="(option, i) in courses" :key="i" :value="option.id">
              {{ option.name }}
            </option>
          </select>
        </div>
        <div class="w-full lg:w-1/4 pr-6">
          <label class="block mb-2">Status</label>
          <select v-model="searchParams.status" class="w-full border hover:border-grey-darker p-3">
            <option :value="null">All</option>
            <option v-for="(option, i) in statuses" :key="i" :value="option.id">
              {{ option.name }}
            </option>
          </select>
        </div>
      </div>

      <div v-if="results.data.length === 0">
        <p class="text-sm">Your search returned no results.</p>
      </div>

      <div v-else class="overflow-x-auto bg-white rounded-lg text-sm">
        <table class="table-auto w-full mb-6 text-left">
          <thead>
            <tr class="uppercase bg-primary text-white">
              <th class=""></th>
              <th class="p-3 text-xs font-normal">Student</th>
              <th class="p-3 text-xs font-normal">Email</th>
              <th class="p-3 text-xs font-normal">Qualification</th>
              <th class="p-3 text-xs font-normal">Status</th>
              <th class="p-3 text-xs font-normal">Progress&nbsp;%</th>
              <th class="p-3 text-xs font-normal">Last&nbsp;Passed</th>
              <th class="p-3 text-xs font-normal">ID&nbsp;Badge</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(associate, i) in results.data">
              <tr :key="i" class="border-t user-row">
                <td class=""><i v-if="associate.enrolments.length > 1" @click="associate.viewMore = !associate.viewMore" class="fas mr-4 text-grey cursor-pointer" :class="associate.viewMore ? 'fa-chevron-up' : 'fa-chevron-down'"></i></td>
                <td @click="associate.viewMore = !associate.viewMore" class="py-3 cursor-pointer underline">{{ associate.name }}</td>
                <td class="py-3">{{ associate.email }}</td>
                <td class="py-3">
                <span v-if="associate.enrolments.length === 1">{{ associate.enrolments[0].course_name }}</span>
                <span v-else @click="associate.viewMore = !associate.viewMore" class="cursor-pointer underline">{{ associate.enrolments.length + ' enrolments' }}</span>
                </td>
                <td class="py-3"><span v-if="associate.enrolments.length === 1">{{ associate.enrolments[0].status }}</span></td>
                <td class="py-3"><span v-if="associate.enrolments.length === 1">{{ associate.enrolments[0].progress }}%</span></td>
                <td class="py-3"><span v-if="associate.enrolments.length === 1">{{ formatDate(associate.enrolments[0].completed_at) }}</span></td>
                <td class="py-3">
                  <span v-if="associate.enrolments.length > 1"></span>
                  <p v-else-if="associate.enrolments[0].id_badge_provided_at">Printed<br />{{ formatDate(associate.enrolments[0].id_badge_provided_at) }}</p>
                  <p v-else-if="associate.enrolments[0].id_badge_requested_at">Requested<br />{{ formatDate(associate.enrolments[0].id_badge_requested_at) }}</p>
                  <p v-else>N/A</p>
                </td>
              </tr>
              <template v-if="associate.enrolments.length > 1 && associate.viewMore === true">
                <tr v-for="(enrolment, j) in associate.enrolments" :key="((i+1)*100)+j" class="">
                  <td class="py-3"></td>
                  <td class="py-3"></td>
                  <td class="py-3"></td>
                  <td class="py-3">{{ associate.enrolments[j].course_name }}</td>
                  <td class="py-3">{{ associate.enrolments[j].status }}</td>
                  <td class="py-3">{{ associate.enrolments[j].progress }}%</td>
                  <td class="py-3">{{ formatDate(associate.enrolments[j].completed_at) }}</td>
                  <td class="py-3">
                    <p v-if="associate.enrolments[j].id_badge_provided_at">Printed<br />{{ formatDate(associate.enrolments[j].id_badge_provided_at) }}</p>
                    <p v-else-if="associate.enrolments[j].id_badge_requested_at">Requested<br />{{ formatDate(associate.enrolments[j].id_badge_requested_at) }}</p>
                    <p v-else>N/A</p>
                  </td>
                </tr>
                <tr :key="((i+1)*100)+associate.enrolments.length">
                  <td colspan="8" class="pb-5"></td>
                </tr>
              </template>
            </template>
          </tbody>
        </table>
        <paginator v-model="searchParams.page" :results="results.meta" />
      </div>
    </panel>
  </div>
</template>

<script>
import { v2Api } from '@/http'
import { debounce } from 'lodash'
import { mapMutations, mapState } from 'vuex'
import moment from 'moment'

export default {
  props: {
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data: function () {
    return {
      searchParams: {
        q: '',
        page: 1,
        course_id: null,
        status: null
      },
      results: null,
      filtered: false,
      pageHasChanged: false,
      courses: [],
      statuses: [
        { 'id': 'completed', 'name': 'Completed' },
        { 'id': 'progress', 'name': 'In Progress' },
        { 'id': 'expired', 'name': 'Expired' },
        { 'id': 'none', 'name': 'Never Accessed' }
      ]
    }
  },
  computed: {
    ...mapState(['selectedOffice'])
  },
  created: function () {
    Object.assign(this.searchParams, this.$route.query)
    this.search()
    this.getCourses()
  },
  methods: {
    ...mapMutations(['setErrorMessage', 'setSuccessMessage']),
    search: debounce(function () {
      this.$router.replace({ query: this.searchParams })
      this.error = null

      if (this.pageHasChanged) {
        this.pageHasChanged = false
      } else {
        this.searchParams.page = 1
      }

      let params = Object.assign(this.searchParams)
      params.office_id = this.selectedOffice.id

      v2Api
        .get('/associate-scheme/users', {
          params: params
        })
        .then(
          response => {
            this.results = response.data
            this.setViewMoreDefaults()
          },
          () => {
            this.setErrorMessage('Failed to load results')
          }
        )
    }, 500),
    getCourses () {
      v2Api.get('/associate-scheme/courses').then((response) => {
        this.courses = response.data
      })
    },
    formatDate (date) {
      if (!date) {
        return 'N/A'
      }

      return moment.utc(date).local().format('DD/MM/YYYY')
    },
    setViewMoreDefaults () {
      this.results.data.forEach(associate => {
        this.$set(associate, 'viewMore', false)
      })
    }
  },
  watch: {
    'searchParams.page': function () {
      this.pageHasChanged = true
    },
    searchParams: {
      deep: true,
      handler: function (newVal) {
        this.filtered = true
        this.search()
      }
    },
    selectedOffice () {
      this.search()
    }
  }
}
</script>

<style lang="scss" scoped>
table tbody {
  tr:nth-child(even) {
    background-color: white !important;
  }
  td {
    padding-top: 0.25rem;
    padding-bottom: 0.25rem;
    padding-left: 0.5rem;
  }
  tr.user-row td {
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
  }
}
</style>
